import React, { useEffect, useMemo, useState } from 'react';
import CachedImg from '../../components/CachedImg';
import { supabase } from '../../lib/supabase';
import { MultiSlotProductPickerSheet } from '../v2/components/MultiSlotProductPickerSheet';
import { FondoPickerSheet, type FondoSelection } from '../v2/FondoPicker';
import { ModelPickerSheet, type CustomModelFields, type ModelSelection } from '../v2/ModelPicker';
import { PosePickerSheet } from '../v2/PosePicker';
import { fetchBibliotecaFondos, fetchFondosGuardados, type FondoBiblioteca, type FondoGuardado } from '../v2/fondosApi';
import { fetchUserModels, type ModelV2 } from '../v2/modelsApi';
import type { MockProductV2 } from '../v2/mockData';
import { fetchAllPoses, type PoseV2 } from '../v2/posesApi';
import { fetchMyCustomPoses, uploadCustomPose } from '../v2/posesCustomApi';
import { fetchUserProducts } from '../v2/productsApi';
import { ProductEditContext, ProductsContext, ProductsLoadedContext } from '../v2/productsContext';
import { guardarSet, type CompanyTest } from './setsApi';
import type { ProviderOverride, TestConfig, TestConfigPayload, TestSet } from './tipos';

/**
 * Editor de sets con los pickers REALES del programa (iteración 2 del feedback):
 * MultiSlotProductPickerSheet (el selector vivo — el ProductSelectorModal viejo es
 * legacy de TestV1 y tiene drift de schema, por eso el 400), ModelPickerSheet
 * (fotos de los modelos guardados), PosePickerSheet (multi + "Mis poses") y
 * FondoPickerSheet abierto en la vista de CATEGORÍAS con ajustes de blanco
 * (temperatura + iluminación). La fidelidad sigue viviendo en buildTestBody.
 *
 * Varios modelos: NO disponible en el harness. El backend YA lo soporta
 * (ADR-2026-08-23-migrar-modelo-mode-multiple: modelo_config[] + composición
 * grupal), pero acá falta la UI de slots por persona — tarea aparte.
 */

const PAYLOAD_DEFAULT: TestConfigPayload = {
  composicion: 'modelo',
  modelo_mode: 'ia',
  background_mode: 'ia',
  formato: '4:5',
};

function payloadDeFondo(sel: FondoSelection): Partial<TestConfigPayload> {
  const limpio: Partial<TestConfigPayload> = {
    background_id: null, background_text: null, background_file_name: null,
    fondo_blanco_iluminacion: null, fondo_blanco_temperatura: null,
  };
  if (sel.mode === 'auto') return { ...limpio, background_mode: 'ia' };
  if (sel.mode === 'blanco')
    return {
      ...limpio, background_mode: 'blanco', background_id: 5,
      fondo_blanco_iluminacion: sel.iluminacion ?? null,
      fondo_blanco_temperatura: sel.temperatura ?? null,
    };
  if (sel.mode === 'biblioteca') return { ...limpio, background_mode: 'elegido', background_id: sel.id };
  if (sel.mode === 'guardado') return { ...limpio, background_mode: 'guardado', background_file_name: sel.fileName };
  return { ...limpio, background_mode: 'descripcion', background_text: sel.text };
}

function fondoDePayload(
  p: TestConfigPayload,
  biblioteca: FondoBiblioteca[],
  guardados: FondoGuardado[],
): FondoSelection | null {
  if (p.background_mode === 'ia') return { mode: 'auto' };
  if (p.background_mode === 'blanco')
    return { mode: 'blanco', iluminacion: p.fondo_blanco_iluminacion ?? undefined, temperatura: p.fondo_blanco_temperatura ?? undefined };
  if (p.background_mode === 'descripcion') return p.background_text ? { mode: 'descripcion', text: p.background_text } : null;
  if (p.background_mode === 'elegido') {
    const f = biblioteca.find((x) => x.id === p.background_id);
    return f ? { mode: 'biblioteca', id: f.id, name: f.nombre, imageUrl: f.imageUrl } : null;
  }
  if (p.background_mode === 'guardado') {
    const g = guardados.find((x) => x.fileName === p.background_file_name);
    return g ? { mode: 'guardado', fileName: g.fileName, name: g.nombre, imageUrl: g.imageUrl } : null;
  }
  return null;
}

function seleccionModelo(p: TestConfigPayload, modelos: ModelV2[]): ModelSelection {
  if (p.modelo_mode === 'guardados' && p.modelo_guardado_id) {
    const m = modelos.find((x) => String(x.id) === p.modelo_guardado_id);
    return { mode: 'preset', id: p.modelo_guardado_id, name: p.modelo_guardado_nombre ?? m?.nombre ?? '', imageUrl: m?.thumbUrl ?? m?.thumbnailUrl ?? '' };
  }
  if (p.modelo_mode === 'personalizado') {
    const fields: CustomModelFields = {
      genero: p.genero, edad: p.edad, tipoCuerpo: p.tipo_cuerpo, tonoPiel: p.tono_piel,
      colorPelo: p.color_pelo, largoPelo: p.largo_pelo, descripcion: p.desc_modelo,
    };
    return { mode: 'custom', fields };
  }
  return { mode: 'auto' };
}

function payloadDeModelo(sel: ModelSelection): Partial<TestConfigPayload> {
  const limpio: Partial<TestConfigPayload> = {
    modelo_guardado_id: null, modelo_guardado_nombre: null,
    genero: undefined, edad: undefined, tipo_cuerpo: undefined, tono_piel: undefined,
    color_pelo: undefined, largo_pelo: undefined, desc_modelo: undefined,
  };
  if (sel.mode === 'preset')
    return { ...limpio, modelo_mode: 'guardados', modelo_guardado_id: sel.id, modelo_guardado_nombre: sel.name };
  if (sel.mode === 'custom')
    return {
      ...limpio, modelo_mode: 'personalizado',
      genero: sel.fields.genero, edad: sel.fields.edad, tipo_cuerpo: sel.fields.tipoCuerpo,
      tono_piel: sel.fields.tonoPiel, color_pelo: sel.fields.colorPelo,
      largo_pelo: sel.fields.largoPelo, desc_modelo: sel.fields.descripcion,
    };
  // 'auto' y 'upload' (flujo sin terminar) caen a IA — igual que la UI real.
  return { ...limpio, modelo_mode: 'ia' };
}

const SetEditor: React.FC<{
  company: CompanyTest;
  sets: TestSet[];
  setActivo: TestSet | null;
  onSeleccionar: (s: TestSet | null) => void;
  onSetsChanged: () => Promise<void>;
}> = ({ company, sets, setActivo, onSeleccionar, onSetsChanged }) => {
  const [productos, setProductos] = useState<MockProductV2[]>([]);
  const [productosLoaded, setProductosLoaded] = useState(false);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [modelos, setModelos] = useState<ModelV2[]>([]);
  const [posesCatalogo, setPosesCatalogo] = useState<PoseV2[]>([]);
  const [posesCustom, setPosesCustom] = useState<PoseV2[]>([]);
  const [biblioteca, setBiblioteca] = useState<FondoBiblioteca[]>([]);
  const [guardados, setGuardados] = useState<FondoGuardado[]>([]);
  const [borrador, setBorrador] = useState<TestSet | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [subiendoPose, setSubiendoPose] = useState(false);
  const [sheetProductos, setSheetProductos] = useState<{ scope: 'set' } | { scope: 'config'; idx: number } | null>(null);
  const [pickerAbierto, setPickerAbierto] = useState<{ tipo: 'pose' | 'fondo' | 'modelo'; configIdx: number } | null>(null);

  const recargarProductos = async (bId: string | null = brandId) => {
    const prods = await fetchUserProducts(company.idCompany, bId);
    setProductos(prods);
    setProductosLoaded(true);
  };

  useEffect(() => {
    void (async () => {
      const { data: brand } = await supabase
        .from('brand_profiles')
        .select('id')
        .eq('id_company', company.idCompany)
        .order('created_at')
        .limit(1)
        .maybeSingle();
      const bId = (brand as { id?: string } | null)?.id ?? null;
      setBrandId(bId);
      const [mods, poses, custom, biblio, guard] = await Promise.all([
        fetchUserModels(company.idCompany),
        fetchAllPoses(),
        fetchMyCustomPoses(company.idCompany),
        fetchBibliotecaFondos(company.rubro),
        fetchFondosGuardados(company.idCompany),
        recargarProductos(bId),
      ]).then(([a, b, c, d, e]) => [a, b, c, d, e] as const);
      setModelos(mods);
      setPosesCatalogo(poses);
      setPosesCustom(custom);
      setBiblioteca(biblio);
      setGuardados(guard);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.idCompany, company.rubro]);

  const productoPorId = useMemo(() => new Map(productos.map((p) => [p.id, p])), [productos]);

  const nuevaConfig = (base?: TestConfig): TestConfig => ({
    config_id: crypto.randomUUID(),
    nombre: base ? `${base.nombre} (copia)` : `Config ${String((borrador?.configs.length ?? 0) + 1)}`,
    payload: base ? { ...base.payload } : { ...PAYLOAD_DEFAULT },
  });

  const guardar = async () => {
    if (!borrador || !borrador.nombre.trim()) return;
    setGuardando(true);
    try {
      const guardado = await guardarSet(company.idCompany, { ...borrador, id: borrador.id || undefined });
      await onSetsChanged();
      onSeleccionar(guardado);
      setBorrador(null);
    } finally {
      setGuardando(false);
    }
  };

  const subirPose = async (file: File) => {
    setSubiendoPose(true);
    try {
      await uploadCustomPose({ userId: company.userId, idCompany: company.idCompany, file });
      setPosesCustom(await fetchMyCustomPoses(company.idCompany));
    } finally {
      setSubiendoPose(false);
    }
  };

  if (!borrador) {
    return (
      <div className="space-y-3">
        <button type="button" onClick={() => setBorrador({ id: '', nombre: 'Set nuevo', variante_ids: [], configs: [nuevaConfig()] })} className="rounded-lg bg-brand-black py-2.5 px-5 text-sm font-medium text-white">
          Nuevo set
        </button>
        {sets.map((s) => (
          <div key={s.id} className={`flex items-center justify-between rounded-xl border bg-white p-4 ${setActivo?.id === s.id ? 'border-brand-black' : 'border-gray-200'}`}>
            <div>
              <div className="font-medium text-brand-black">{s.nombre}</div>
              <div className="text-xs text-gray-500">{s.variante_ids.length} productos · {s.configs.length} configs</div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => onSeleccionar(s)} className="rounded-lg border border-gray-300 py-2 px-4 text-sm text-brand-black hover:border-gray-500">Usar</button>
              <button type="button" onClick={() => setBorrador(s)} className="rounded-lg border border-gray-300 py-2 px-4 text-sm text-brand-black hover:border-gray-500">Editar</button>
            </div>
          </div>
        ))}
        {sets.length === 0 && <p className="text-sm text-gray-500">Todavía no hay sets guardados.</p>}
      </div>
    );
  }

  const setConfig = (i: number, config: TestConfig) => {
    const configs = [...borrador.configs];
    configs[i] = config;
    setBorrador({ ...borrador, configs });
  };
  const configDelPicker = pickerAbierto ? borrador.configs[pickerAbierto.configIdx] : null;
  // Selección que ve el sheet de productos según el scope (set o una config).
  const idsDelSheet =
    sheetProductos?.scope === 'config'
      ? (borrador.configs[sheetProductos.idx]?.variante_ids ?? [])
      : borrador.variante_ids;
  const seleccionActual = idsDelSheet
    .map((id) => productoPorId.get(id))
    .filter((x): x is MockProductV2 => !!x);

  return (
    <ProductsContext.Provider value={productos}>
      <ProductsLoadedContext.Provider value={productosLoaded}>
        <ProductEditContext.Provider value={{ companyId: company.idCompany, refreshProducts: () => void recargarProductos() }}>
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <input value={borrador.nombre} onChange={(e) => setBorrador({ ...borrador, nombre: e.target.value })} className="rounded-lg border border-gray-300 py-2 px-4 text-sm text-brand-black" placeholder="Nombre del set" />
              <button type="button" onClick={() => void guardar()} disabled={guardando} className="rounded-lg bg-brand-black py-2 px-4 text-sm font-medium text-white disabled:opacity-50">
                {guardando ? 'Guardando…' : 'Guardar set'}
              </button>
              <button type="button" onClick={() => setBorrador(null)} className="rounded-lg border border-gray-300 py-2 px-4 text-sm text-brand-black">Cancelar</button>
            </div>

            <section>
              <div className="mb-2 flex items-center gap-3">
                <h2 className="text-sm font-semibold text-brand-black">Productos del set ({borrador.variante_ids.length})</h2>
                <button type="button" onClick={() => setSheetProductos({ scope: 'set' })} className="rounded-lg border border-gray-300 py-2 px-4 text-xs text-brand-black hover:border-gray-500">
                  Elegir productos
                </button>
                <span className="text-xs text-gray-400">default — cada config puede tener los suyos</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {borrador.variante_ids.map((id) => {
                  const meta = productoPorId.get(id);
                  return (
                    <div key={id} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white py-1 pl-1 pr-2">
                      {meta?.imageUrl ? (
                        <CachedImg src={meta.imageUrl} alt="" className="h-8 w-8 rounded object-cover" />
                      ) : (
                        <div className="h-8 w-8 rounded bg-gray-100" />
                      )}
                      <span className="max-w-32 truncate text-xs text-brand-black">{meta?.name ?? id.slice(0, 8)}</span>
                      <button type="button" onClick={() => setBorrador({ ...borrador, variante_ids: borrador.variante_ids.filter((x) => x !== id) })} className="text-gray-400 hover:text-red-600">×</button>
                    </div>
                  );
                })}
                {borrador.variante_ids.length === 0 && <p className="text-xs text-gray-500">Sin productos — abrí "Elegir productos".</p>}
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-brand-black">Configs ({borrador.configs.length})</h2>
              {borrador.configs.map((config, i) => (
                <ConfigForm
                  key={config.config_id}
                  config={config}
                  modelos={modelos}
                  posesCatalogo={posesCatalogo}
                  posesCustom={posesCustom}
                  onChange={(c) => setConfig(i, c)}
                  onAbrirPoses={() => setPickerAbierto({ tipo: 'pose', configIdx: i })}
                  onAbrirFondo={() => setPickerAbierto({ tipo: 'fondo', configIdx: i })}
                  onAbrirModelo={() => setPickerAbierto({ tipo: 'modelo', configIdx: i })}
                  onAbrirProductos={() => setSheetProductos({ scope: 'config', idx: i })}
                  productoPorId={productoPorId}
                  productosDelSet={borrador.variante_ids.length}
                  onDuplicar={() => setBorrador({ ...borrador, configs: [...borrador.configs, nuevaConfig(config)] })}
                  onBorrar={() => setBorrador({ ...borrador, configs: borrador.configs.filter((_, j) => j !== i) })}
                />
              ))}
              <button type="button" onClick={() => setBorrador({ ...borrador, configs: [...borrador.configs, nuevaConfig()] })} className="rounded-lg border border-dashed border-gray-400 py-2 px-4 text-sm text-brand-black">
                + Agregar config
              </button>
              <p className="text-xs text-gray-400">
                Varios modelos en una misma imagen: todavía no disponible en el harness (falta la UI de slots por persona).
              </p>
            </section>

            <MultiSlotProductPickerSheet
              open={sheetProductos !== null}
              onClose={() => setSheetProductos(null)}
              currentSelection={seleccionActual}
              initialSelection={seleccionActual}
              directToPick
              onConfirm={(prods) => {
                const ids = prods.map((p) => p.id);
                if (sheetProductos?.scope === 'config') {
                  const configs = [...borrador.configs];
                  const c = configs[sheetProductos.idx]!;
                  configs[sheetProductos.idx] = { ...c, variante_ids: ids };
                  setBorrador({ ...borrador, configs });
                } else {
                  setBorrador({ ...borrador, variante_ids: ids });
                }
                setSheetProductos(null);
              }}
              userCompanyId={company.idCompany}
              brandId={brandId}
              rubro={company.rubro}
              userPlan={company.plan}
              onProductsRefreshed={() => void recargarProductos()}
              title="Productos del test"
              pickDescription="Los productos sobre los que corre cada config del set."
            />

            {configDelPicker && pickerAbierto?.tipo === 'modelo' && (
              <ModelPickerSheet
                open
                selection={seleccionModelo(configDelPicker.payload, modelos)}
                onChange={(sel) =>
                  setConfig(pickerAbierto.configIdx, {
                    ...configDelPicker,
                    payload: { ...configDelPicker.payload, ...payloadDeModelo(sel) },
                  })
                }
                onClose={() => setPickerAbierto(null)}
                userModels={modelos}
                userId={company.userId}
                companyId={company.idCompany}
                onModelCreated={() => void fetchUserModels(company.idCompany).then(setModelos)}
              />
            )}

            {configDelPicker && pickerAbierto?.tipo === 'pose' && (
              <PosePickerSheet
                open
                poses={posesCatalogo}
                customPoses={posesCustom}
                selectedIds={[...(configDelPicker.payload.poses ?? []), ...(configDelPicker.payload.poses_custom ?? []).map((c) => c.id)]}
                onChange={(ids) => {
                  const custom = posesCustom.filter((p) => ids.includes(p.id));
                  const normales = ids.filter((id) => !custom.some((c) => c.id === id));
                  setConfig(pickerAbierto.configIdx, {
                    ...configDelPicker,
                    payload: {
                      ...configDelPicker.payload,
                      poses: normales,
                      poses_custom: custom.map((c) => ({ id: c.id, filename: c.filename, nombre: c.nombre })),
                    },
                  });
                }}
                onClose={() => setPickerAbierto(null)}
                onUploadPose={(file) => void subirPose(file)}
                uploading={subiendoPose}
              />
            )}

            {configDelPicker && pickerAbierto?.tipo === 'fondo' && (
              <FondoPickerSheet
                open
                initialView="all"
                selection={fondoDePayload(configDelPicker.payload, biblioteca, guardados)}
                onChange={(sel) =>
                  setConfig(pickerAbierto.configIdx, {
                    ...configDelPicker,
                    payload: { ...configDelPicker.payload, ...payloadDeFondo(sel) },
                  })
                }
                onClose={() => setPickerAbierto(null)}
                biblioteca={biblioteca}
                guardados={guardados}
                companyId={company.idCompany}
                onFondoSaved={() => void fetchFondosGuardados(company.idCompany).then(setGuardados)}
                mostrarAjustesBlanco
              />
            )}
          </div>
        </ProductEditContext.Provider>
      </ProductsLoadedContext.Provider>
    </ProductsContext.Provider>
  );
};

const Campo: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-xs text-gray-500">
    {label}
    {children}
  </label>
);
const selectCls = 'rounded-md border border-gray-300 bg-white py-1.5 px-2 text-sm text-brand-black';
const btnPickerCls = 'rounded-md border border-gray-300 bg-white py-1.5 px-2 text-left text-sm text-brand-black hover:border-gray-500';

const ConfigForm: React.FC<{
  config: TestConfig;
  modelos: ModelV2[];
  posesCatalogo: PoseV2[];
  posesCustom: PoseV2[];
  onChange: (c: TestConfig) => void;
  onAbrirPoses: () => void;
  onAbrirFondo: () => void;
  onAbrirModelo: () => void;
  onAbrirProductos: () => void;
  onDuplicar: () => void;
  onBorrar: () => void;
  productoPorId: Map<string, MockProductV2>;
  productosDelSet: number;
}> = ({ config, modelos, posesCatalogo, posesCustom, onChange, onAbrirPoses, onAbrirFondo, onAbrirModelo, onAbrirProductos, onDuplicar, onBorrar, productoPorId, productosDelSet }) => {
  const p = config.payload;
  const set = (cambios: Partial<TestConfigPayload>) => onChange({ ...config, payload: { ...p, ...cambios } });
  const nPoses = (p.poses?.length ?? 0) + (p.poses_custom?.length ?? 0);
  const modeloGuardado = p.modelo_mode === 'guardados' ? modelos.find((m) => String(m.id) === p.modelo_guardado_id) : null;
  const etiquetaModelo =
    p.modelo_mode === 'guardados' ? (p.modelo_guardado_nombre ?? modeloGuardado?.nombre ?? '— elegir —')
    : p.modelo_mode === 'personalizado' ? [p.genero, p.edad].filter(Boolean).join(', ') || 'Personalizado'
    : 'IA (automático)';
  const etiquetaFondo =
    p.background_mode === 'ia' ? 'Automático'
    : p.background_mode === 'blanco' ? `Fondo blanco${p.fondo_blanco_iluminacion ? ` · ${p.fondo_blanco_iluminacion}` : ''}${p.fondo_blanco_temperatura ? ` · ${p.fondo_blanco_temperatura}` : ''}`
    : p.background_mode === 'descripcion' ? `“${(p.background_text ?? '').slice(0, 30)}…”`
    : p.background_mode === 'guardado' ? 'Escena guardada'
    : `Biblioteca #${String(p.background_id ?? '?')}`;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <input value={config.nombre} onChange={(e) => onChange({ ...config, nombre: e.target.value })} className="flex-1 rounded-md border border-gray-300 py-1.5 px-2 text-sm font-medium text-brand-black" />
        <button type="button" onClick={onDuplicar} className="rounded-md border border-gray-300 py-1.5 px-3 text-xs text-brand-black">Duplicar</button>
        <button type="button" onClick={onBorrar} className="rounded-md border border-gray-300 py-1.5 px-3 text-xs text-red-600">Borrar</button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Campo label="Productos">
          <button type="button" onClick={onAbrirProductos} className={btnPickerCls}>
            {config.variante_ids?.length ? `Propios (${config.variante_ids.length})` : `Los del set (${productosDelSet})`}
          </button>
          {(config.variante_ids?.length ?? 0) > 0 && (
            <>
              <div className="flex flex-wrap gap-1">
                {config.variante_ids!.map((id) => {
                  const prod = productoPorId.get(id);
                  return (
                    <span key={id} className="group relative" title={prod?.name ?? id}>
                      {prod?.imageUrl ? (
                        <CachedImg src={prod.imageUrl} alt={prod.name} className="h-10 w-10 rounded-md border border-gray-200 object-cover" />
                      ) : (
                        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-gray-100 text-[9px] text-gray-400">?</span>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const restantes = config.variante_ids!.filter((x) => x !== id);
                          onChange({ ...config, variante_ids: restantes.length ? restantes : undefined });
                        }}
                        className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-brand-black text-[9px] leading-none text-white group-hover:flex"
                        aria-label="Quitar producto"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
              <button type="button" onClick={() => onChange({ ...config, variante_ids: undefined })} className="text-left text-[10px] text-blue-600 underline">
                volver a usar los del set
              </button>
            </>
          )}
        </Campo>
        <Campo label="Composición">
          <select className={selectCls} value={p.composicion} onChange={(e) => set({ composicion: e.target.value as TestConfigPayload['composicion'] })}>
            <option value="modelo">Con modelo</option>
            <option value="producto">Solo producto</option>
          </select>
        </Campo>

        {p.composicion === 'modelo' && (
          <>
            <Campo label="Modelo">
              <button type="button" onClick={onAbrirModelo} className={btnPickerCls}>
                <span className="flex items-center gap-2">
                  {modeloGuardado?.thumbUrl && <CachedImg src={modeloGuardado.thumbUrl} alt="" className="h-6 w-6 rounded-full object-cover" />}
                  <span className="truncate">{etiquetaModelo}</span>
                </span>
              </button>
            </Campo>
            <Campo label={`Poses (${nPoses})`}>
              <button type="button" onClick={onAbrirPoses} className={btnPickerCls}>
                {nPoses === 0 ? 'Default (Editorial de pie)' : `${nPoses} seleccionadas`}
              </button>
              {nPoses > 0 && (
                <div className="flex flex-wrap gap-1">
                  {[
                    ...(p.poses ?? []).map((id) => ({ id, pose: posesCatalogo.find((x) => x.id === id), esCustom: false })),
                    ...(p.poses_custom ?? []).map((c) => ({ id: c.id, pose: posesCustom.find((x) => x.id === c.id), esCustom: true })),
                  ].map(({ id, pose, esCustom }) => (
                    <span key={id} className="group relative" title={pose?.nombre ?? (esCustom ? 'pose propia' : id)}>
                      {pose?.imageUrl ? (
                        <CachedImg src={pose.imageUrl} alt={pose.nombre} className="h-10 w-10 rounded-md border border-gray-200 object-cover" />
                      ) : (
                        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-gray-100 text-[9px] text-gray-400">?</span>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          set(
                            esCustom
                              ? { poses_custom: (p.poses_custom ?? []).filter((c) => c.id !== id) }
                              : { poses: (p.poses ?? []).filter((x) => x !== id) },
                          )
                        }
                        className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-brand-black text-[9px] leading-none text-white group-hover:flex"
                        aria-label="Quitar pose"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </Campo>
          </>
        )}

        {p.composicion === 'producto' && (
          <Campo label="Vista">
            <select className={selectCls} value={p.vista_producto ?? 'frente'} onChange={(e) => set({ vista_producto: e.target.value })}>
              {['frente', 'posterior', 'lateral', 'maniqui_frontal', 'maniqui_mujer_frontal', 'prenda_doblada'].map((v) => (<option key={v} value={v}>{v}</option>))}
            </select>
          </Campo>
        )}

        <Campo label="Fondo">
          <button type="button" onClick={onAbrirFondo} className={btnPickerCls}>{etiquetaFondo}</button>
        </Campo>

        <Campo label="Formato">
          <select className={selectCls} value={p.formato} onChange={(e) => set({ formato: e.target.value as TestConfigPayload['formato'] })}>
            {['1:1', '4:5', '9:16', '16:9'].map((f) => (<option key={f}>{f}</option>))}
          </select>
        </Campo>
        <Campo label="Proveedor">
          <select className={selectCls} value={p.provider_override ?? ''} onChange={(e) => set({ provider_override: (e.target.value || null) as ProviderOverride | null })}>
            <option value="">Cadena normal (con fallback)</option>
            <option value="gemini">Solo Gemini</option>
            <option value="openai">Solo OpenAI</option>
            <option value="seedream">Solo Seedream</option>
          </select>
        </Campo>
        <Campo label="Instrucción adicional">
          <input className={selectCls} value={p.instruccion_adicional ?? ''} onChange={(e) => set({ instruccion_adicional: e.target.value || null })} placeholder="opcional" />
        </Campo>
      </div>
    </div>
  );
};

export default SetEditor;
